/**
 * What scrolling does to the app's chrome on a phone (the user, 2026-10-07).
 *
 * - **The header gets out of the way.** Scrolling down slides it up off the
 *   screen; scrolling back UP brings it back. Only a real scroll up does
 *   (the user, 2026-10-27: "the header returns while not scrolling — I said it
 *   returns when scrolling up"): one that somebody's finger, wheel or keys
 *   started, at least `SHOW_AFTER` px of it. The page moving on its own — the
 *   bounce at the bottom, a picture loading above, a list growing — is not
 *   somebody scrolling up. The page is what somebody is reading — on a 667px
 *   phone the bar is a tenth of it.
 * - **Pulling down at the top reloads.** `overscroll-behavior-y: none` on the
 *   page (tokens.css) switches off the browser's own pull-to-refresh, and an
 *   installed web app never had one, so the shell draws its own. It refetches
 *   what is ON SCREEN — every active query, the rave's bundle included —
 *   rather than reloading the page: the same fresh data, no white flash, and
 *   with no signal the stored copy simply stays.
 *
 * Both are phone-width only (< 880px, where the menu is a modal drawer, not a
 * pinned column hanging off the header). The header state is an attribute on
 * `<html>`, so the CSS that moves it — and the sticky bars pinned under it —
 * is one rule each, and nothing below the shell has to know.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useT } from '@/i18n';

const PHONE = '(max-width: 879px)';
/** Below this the header never hides: the page has hardly moved. */
const HIDE_AFTER = 64;
/** Down this far in one go before it hides — a twitch is not a scroll. */
const HIDE_DELTA = 6;
/** Up this far, all together, before it comes back. */
export const SHOW_AFTER = 24;
/** A scroll this long after a touch, wheel or key is still theirs — a flick's glide included. */
const INPUT_MS = 1500;
/** How far to pull (after the drag is damped) before letting go reloads. */
const PULL_TRIGGER = 64;
const PULL_MAX = 96;
/** Long enough to see that something happened, however fast the answer. */
const MIN_SPIN_MS = 600;

const isPhone = () => typeof window.matchMedia === 'function' && window.matchMedia(PHONE).matches;

/** The search or the menu is open: the header must stay where it is. */
const headerInUse = () =>
  // `data-header-hold`: a screen has just scrolled something to the top under the header (Live's lit card).
  document.documentElement.dataset.headerHold !== undefined
  || Boolean(document.querySelector('.shell-find[data-open], .shell-toggle[data-open]'))
  || Boolean(document.activeElement?.closest('header'));

/**
 * `data-header-away` on `<html>` while the header is scrolled off the top.
 * `pinned` (the menu is open) brings it back and holds it.
 */
export function useHeaderAway(pinned: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    if (pinned) { delete root.dataset.headerAway; return; }
    // Past the end (the bounce at the bottom) counts as the end: coming back from it is not up.
    const bottom = () => Math.max(0, (document.scrollingElement ?? root).scrollHeight - window.innerHeight);
    const at = () => Math.min(window.scrollY, bottom());
    let last = at();
    let up = 0;
    let input = 0;
    const onInput = () => { input = Date.now(); };
    const onScroll = () => {
      const y = at();
      const delta = y - last;
      last = y;
      if (!isPhone() || y < HIDE_AFTER || headerInUse()) { up = 0; delete root.dataset.headerAway; return; }
      if (delta > 0) {
        up = 0;
        if (delta > HIDE_DELTA) root.dataset.headerAway = '';
        return;
      }
      if (delta === 0 || Date.now() - input > INPUT_MS) return;
      up -= delta;
      if (up >= SHOW_AFTER) { up = 0; delete root.dataset.headerAway; }
    };
    const inputs = ['touchstart', 'touchmove', 'wheel', 'keydown'] as const;
    for (const e of inputs) window.addEventListener(e, onInput, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      for (const e of inputs) window.removeEventListener(e, onInput);
      window.removeEventListener('scroll', onScroll);
      delete root.dataset.headerAway;
    };
  }, [pinned]);
}

/**
 * A touch that should not pull: it began in a dialog, a field, the map (a
 * drag there pans it), or inside anything of its own that is scrolled down.
 */
function ownsTheDrag(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  if (el?.closest('[role="dialog"], input, textarea, select, .map-canvas, [data-no-pull]')) return true;
  for (; el && el !== document.body; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return false;
}

export function PullToRefresh() {
  const tr = useT();
  const queryClient = useQueryClient();
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const pullRef = useRef(0);
  const busyRef = useRef(false);

  useEffect(() => {
    let startY: number | null = null;
    const set = (v: number) => { pullRef.current = v; setPull(v); };

    const onStart = (e: TouchEvent) => {
      startY = null;
      if (busyRef.current || !isPhone() || e.touches.length !== 1) return;
      if (window.scrollY > 0 || document.body.style.overflow === 'hidden') return;
      if (ownsTheDrag(e.target)) return;
      startY = e.touches[0]?.clientY ?? null;
    };
    const onMove = (e: TouchEvent) => {
      if (startY === null) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dy = touch.clientY - startY;
      // Moved the page instead, or went up: not a pull.
      if (window.scrollY > 0 || dy <= 0) { if (pullRef.current) set(0); return; }
      set(Math.min(PULL_MAX, dy * 0.5));
    };
    const onEnd = () => {
      if (startY === null) return;
      startY = null;
      const go = pullRef.current >= PULL_TRIGGER;
      set(0);
      if (!go) return;
      busyRef.current = true;
      setBusy(true);
      void Promise.all([
        queryClient.refetchQueries({ type: 'active' }).catch(() => undefined),
        new Promise((r) => setTimeout(r, MIN_SPIN_MS)),
      ]).finally(() => { busyRef.current = false; setBusy(false); });
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [queryClient]);

  if (!pull && !busy) return null;
  const ready = pull >= PULL_TRIGGER;
  return (
    <div
      className="shell-ptr"
      data-busy={busy || undefined}
      data-ready={ready || undefined}
      style={{ '--ptr-y': `${busy ? PULL_TRIGGER : pull}px`, '--ptr-turn': `${(pull / PULL_TRIGGER) * 270}deg` } as React.CSSProperties}
      role={busy ? 'status' : undefined}
      aria-live="polite"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 12a8 8 0 1 1-2.34-5.66" /><path d="M20 4v5h-5" />
      </svg>
      {busy && <span className="ui-sr-only">{tr('Refreshing…')}</span>}
    </div>
  );
}

/**
 * `data-filters-away` on `<html>` while a page's sticky filters are scrolled away (the user, 2026-11-02: "the schedule
 * scrolling down scrolls all the filters away too; scrolling up again brings them back") — on every width, by the
 * header's own rule: away on a scroll down, back on a real scroll up of `SHOW_AFTER`.
 */
export function useFiltersAway(on: boolean) {
  useEffect(() => {
    const root = document.documentElement;
    if (!on) { delete root.dataset.filtersAway; return undefined; }
    const bottom = () => Math.max(0, (document.scrollingElement ?? root).scrollHeight - window.innerHeight);
    const at = () => Math.min(window.scrollY, bottom());
    let last = at();
    let up = 0;
    let input = 0;
    const onInput = () => { input = Date.now(); };
    const onScroll = () => {
      const y = at();
      const delta = y - last;
      last = y;
      if (y < HIDE_AFTER) { up = 0; delete root.dataset.filtersAway; return; }
      if (delta > 0) {
        up = 0;
        // Only a person's scroll sends them away — never the page's own (the Timeline opening on now).
        if (delta > HIDE_DELTA && Date.now() - input <= INPUT_MS) root.dataset.filtersAway = '';
        return;
      }
      if (delta === 0 || Date.now() - input > INPUT_MS) return;
      up -= delta;
      if (up >= SHOW_AFTER) { up = 0; delete root.dataset.filtersAway; }
    };
    const inputs = ['touchstart', 'touchmove', 'wheel', 'keydown'] as const;
    for (const e of inputs) window.addEventListener(e, onInput, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      for (const e of inputs) window.removeEventListener(e, onInput);
      window.removeEventListener('scroll', onScroll);
      delete root.dataset.filtersAway;
    };
  }, [on]);
}
